import _ from 'underscore';

import { Messages } from '~src/codegen/Localize';

import { RemoteRefreshAgents } from '../../extractRefresh/resources/RemoteRefreshAgents';
import { IGetRemoteRefreshAgentsResult, IRemoteRefreshAgent } from '../../extractRefresh/serverApi/requests/GetRemoteRefreshAgentsRequest';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { ChangeDatasourceRemoteQueryAgentDialog } from '../components/ChangeDatasourceRemoteQueryAgentDialog';
import { IDataSourceDetailInfo } from '../models/IDataSourceInfo';

export interface IRemoteQueryAgentOption {
  display: string;
  remoteAgentName: string;
}

export namespace ChangeDatasourceRemoteQueryAgentAction {

  export function execute(datasource: IDataSourceDetailInfo): Promise<IResult> {

    // Special option value: Associate this data source with whatever the site default is
    const useDefaultOptionId: string = '$default$';

    // Special option value: Do not associate any Bridge client and disable remoting live connections for this data source
    const useDisabledOptionId: string = '$disabled$';

    return getRemoteQueryAgentOptions(datasource, useDefaultOptionId, useDisabledOptionId)
      .then(remoteQueryAgentOptions => {

        const initialSelectedAgent = getDefaultAgentOption(datasource, useDefaultOptionId, useDisabledOptionId);
        const useDefaultOrDisabledAgentFunc = (selectedAgentName: string) => { return useDefaultOptionId === selectedAgentName || useDisabledOptionId === selectedAgentName; };

        return ChangeDatasourceRemoteQueryAgentDialog.open(
          datasource,
          remoteQueryAgentOptions,
          initialSelectedAgent,
          useDefaultOrDisabledAgentFunc);
      });
  }

  export function getRemoteQueryAgentOptions(datasource: IDataSourceDetailInfo, useDefaultOptionId: string, useDisabledOptionId: string) {
    return RemoteRefreshAgents.get()
      .then((result: IGetRemoteRefreshAgentsResult) => {

        // Build a list of agents, it should include
        // 1. all agents returned from server side
        // 2. current assigned agent, it may not be returned by server
        let listContainsExistingAgent = false;

        let remoteQueryAgentOptions: IRemoteQueryAgentOption[] = _.map(result.agents, (agent: IRemoteRefreshAgent) => {
          if (datasource.remoteAgentName && datasource.remoteAgentName === agent.name) {
            listContainsExistingAgent = true;
          }
          return { display: agent.name, remoteAgentName: agent.name };
        });

        // Add current agent if not listed by Vizportal
        if (!listContainsExistingAgent && datasource && datasource.remoteAgentName) {
          remoteQueryAgentOptions.unshift({ display: datasource.remoteAgentName, remoteAgentName: datasource.remoteAgentName });
        }

        // Add the option for reverting to the site default
        remoteQueryAgentOptions.unshift({
          display: Messages.datasourceRemoteQueryAgentSiteDefaultOption(),
          remoteAgentName: useDefaultOptionId
        });

        // Add the option for assigning no live sync client & disabling remoting
        remoteQueryAgentOptions.unshift({
          display: Messages.datasourceRemoteQueryAgentDisabledOption(),
          remoteAgentName: useDisabledOptionId
        });

        return remoteQueryAgentOptions;
      });
  }

  export function getDefaultAgentOption(datasource: IDataSourceDetailInfo, useDefaultOptionId: string, useDisabledOptionId: string) {
    let remoteAgentName = '';

    // Select default option when site default is used
    if (datasource.remoteQueryAgentSiteDefault) {
      remoteAgentName = useDefaultOptionId;
    }
    // Select default option when site default is used (but no site default agent is assigned)
    else if (!datasource.remoteAgentName && !datasource.testingConnectionAllowed) {
      remoteAgentName = useDefaultOptionId;
    }
    // Select disabled option when remoting live connections is off
    else if (!datasource.remoteAgentName) {
      remoteAgentName = useDisabledOptionId;
    }
    else {
      remoteAgentName = datasource.remoteAgentName;
    }
    return remoteAgentName;
  }
}
